import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Grid,
  Stack,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { 
  Search, 
  Refresh, 
  Phone, 
  AccountBalance,
  MoneyOff,
  TrendingUp,
  Close,
  Person,
  CalendarToday,
  Receipt,
  AccountCircle,
  Store,
  ArrowUpward,
  ArrowDownward,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { membersWithCreditBalance , fetchFundingHistoryApi, zeroFundingHistoryApi} from "../../../api/customers/customers";

interface Creditor {
  user_id: string;
  phone: string;
  customer_name: string;
  total_credit: number;
}

interface FundingHistory {
  dateCreated: string;
  crValue: string;
  drValue: string;
  email: string;
  full_name: string;
  phone: string;
  shopID: string;
  staff_name: string;
  up_user_id: string;
  wl_last_updated: string;
  wl_user_id: string;
  wl_walletID: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const SundryCreditorsPage: React.FC = () => {
  const [creditors, setCreditors] = useState<Creditor[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCreditor, setSelectedCreditor] = useState<Creditor | null>(null);
  
  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [creditorToZero, setCreditorToZero] = useState<Creditor | null>(null);
  const [zeroingLoading, setZeroingLoading] = useState(false);

  // State for snackbar notifications
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [fundingHistory, setFundingHistory] = useState<FundingHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchCreditors = async () => {
    setStatus({ loading: true, error: false });
    try {
      const response = await membersWithCreditBalance();
      console.log("Creditors response:", response);
      setCreditors(response.data || []); 
    } catch (error) {
      console.error("Error fetching creditors:", error);
      setStatus({ loading: false, error: true });
      showSnackbar('Error fetching creditors. Please try again.', 'error');
    } finally {
      setStatus({ loading: false, error: false });
    }
  };

  useEffect(() => {
    fetchCreditors();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchCreditors();
  };

  const fetchFundingHistory = async (userId: string) => {
    setHistoryLoading(true);
    try {
      const response = await fetchFundingHistoryApi(userId);
      console.log("Funding history response:", response);
      setFundingHistory(response || []); 
    } catch (error) {
      console.error("Error fetching funding history:", error);
      showSnackbar('Error loading transaction history.', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleZeroAccountClick = (creditor: Creditor) => {
    // Store the creditor and open confirmation dialog
    setCreditorToZero(creditor);
    setConfirmDialogOpen(true);
  };

  const handleConfirmZeroAccount = async () => {
    if (!creditorToZero) return;
    
    setZeroingLoading(true);
    try {
      console.log("Initiating zero funding history for userId:", creditorToZero.user_id);
      const response = await zeroFundingHistoryApi(creditorToZero.user_id);
      console.log("Zero funding history response:", response);
      
      // Refresh the creditors list to show updated balance
      fetchCreditors();
      
      // Show success message
      showSnackbar(
        `Account for ${cleanCustomerName(creditorToZero.customer_name)} has been successfully zeroed.`,
        'success'
      );
      
    } catch (error: any) {
      console.error("Error zeroing account:", error);
      
      // Show error message with more detail if available
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || 'Error zeroing account. Please try again.';
      
      showSnackbar(errorMessage, 'error');
      
    } finally {
      setZeroingLoading(false);
      setConfirmDialogOpen(false);
      setCreditorToZero(null);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setCreditorToZero(null);
  };

  // Snackbar functions
  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (selectedCreditor) {
      fetchFundingHistory(selectedCreditor.user_id);
    }
  }, [selectedCreditor]);

  const filteredCreditors = creditors.filter((creditor) => {
    const customerName = (creditor.customer_name || "").toLowerCase().trim();
    const phone = (creditor.phone || "").toLowerCase();
    const query = searchQuery.toLowerCase();

    return (
      customerName.includes(query) ||
      phone.includes(query)
    );
  });

  // Calculate totals
  const totalCredit = filteredCreditors.reduce((sum, creditor) => {
    return sum + (creditor.total_credit || 0);
  }, 0);

  const totalCreditors = filteredCreditors.length;

  const formatCurrency = (amount: number) => {
    return `₦${(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getCreditSeverity = (amount: number) => {
    if (amount === 0) return "success";
    if (amount < 50000) return "warning";
    return "error";
  };

  const cleanCustomerName = (name: string) => {
    if (!name) return "Unknown Creditor";
    return name.replace(/\s+/g, ' ').trim();
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedCreditor(null);
    setFundingHistory([]);
  };

  // Calculate current balance from all transactions (drValue = inflow, crValue = outflow)
  const calculateCurrentBalance = () => {
    return fundingHistory.reduce((balance, transaction) => {
      const inflow = parseFloat(transaction.drValue || "0"); // drValue is inflow
      const outflow = parseFloat(transaction.crValue || "0"); // crValue is outflow
      return balance + inflow - outflow;
    }, 0);
  };

  // Calculate total inflow and outflow (drValue = inflow, crValue = outflow)
  const calculateTotals = () => {
    return fundingHistory.reduce((totals, transaction) => {
      const inflow = parseFloat(transaction.drValue || "0"); // drValue is inflow
      const outflow = parseFloat(transaction.crValue || "0"); // crValue is outflow
      return {
        totalInflow: totals.totalInflow + inflow,
        totalOutflow: totals.totalOutflow + outflow
      };
    }, { totalInflow: 0, totalOutflow: 0 });
  };

  const { totalInflow, totalOutflow } = calculateTotals();
  const currentBalance = calculateCurrentBalance();

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Sundry Creditors</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonLoading
          isOpen={status.loading}
          message={"Fetching creditors..."}
          spinner="circles"
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
            iconMapping={{
              success: <CheckCircle fontSize="inherit" />,
              error: <ErrorIcon fontSize="inherit" />,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog for Zero Account */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCloseConfirmDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Confirm Zero Account
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action will reset the account balance to zero. This action cannot be undone.
            </Alert>
            {creditorToZero && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to zero the account for:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {cleanCustomerName(creditorToZero.customer_name)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {creditorToZero.phone || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Balance: {formatCurrency(creditorToZero.total_credit)}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseConfirmDialog} 
              variant="outlined"
              disabled={zeroingLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmZeroAccount} 
              variant="contained" 
              color="warning"
              disabled={zeroingLoading}
              startIcon={zeroingLoading ? <CircularProgress size={20} /> : null}
            >
              {zeroingLoading ? "Processing..." : "Yes, Zero Account"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Funding History Dialog */}
        <Dialog
          open={historyDialogOpen}
          onClose={handleCloseHistoryDialog}
          maxWidth="lg"
          fullWidth
          scroll="paper"
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <AccountCircle sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Account Statement - {selectedCreditor?.customer_name}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseHistoryDialog}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent dividers>
            {historyLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <Box textAlign="center">
                  <CircularProgress size={60} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Loading account statement...
                  </Typography>
                </Box>
              </Box>
            ) : fundingHistory.length > 0 ? (
              <Box>
                {/* Account Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          <ArrowDownward sx={{ mr: 1 }} />
                          <Typography variant="body2">Total Inflow (DR)</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(totalInflow)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          <ArrowUpward sx={{ mr: 1 }} />
                          <Typography variant="body2">Total Outflow (CR)</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(totalOutflow)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      bgcolor: currentBalance >= 0 ? 'primary.light' : 'warning.light', 
                      color: 'white' 
                    }}>
                      <CardContent>
                        <Typography variant="body2">Current Balance</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(currentBalance)}
                        </Typography>
                        <Chip 
                          label={currentBalance >= 0 ? "Positive Balance" : "Credit Balance"} 
                          size="small"
                          sx={{ 
                            mt: 1,
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Account Information */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <Person sx={{ mr: 1 }} />
                          Account Holder
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Full Name:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.full_name || selectedCreditor?.customer_name}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Phone:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.phone || selectedCreditor?.phone}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Email:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.email || 'N/A'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Wallet ID:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.wl_walletID || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <Store sx={{ mr: 1 }} />
                          Account Details
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">User ID:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.wl_user_id || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Account Created:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(fundingHistory[0]?.dateCreated)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(fundingHistory[0]?.wl_last_updated)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Shop:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {fundingHistory[0]?.shopID || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Bank Statement Table */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Receipt sx={{ mr: 1 }} />
                      Transaction History ({fundingHistory.length} transactions)
                    </Typography>
                    
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold">Date & Time</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold">Description</Typography></TableCell>
                          <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold" color="success.main">Inflow (DR)</Typography></TableCell>
                          <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold" color="error.main">Outflow (CR)</Typography></TableCell>
                          <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Balance</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold">Processed By</Typography></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fundingHistory.map((entry, index) => {
                            const inflow = parseFloat(entry.drValue || "0"); // drValue is inflow
                            const outflow = parseFloat(entry.crValue || "0"); // crValue is outflow
                            const transactionAmount = inflow - outflow;
                            
                            // Calculate running balance
                            const previousTransactions = fundingHistory.slice(0, index);
                            const previousBalance = previousTransactions.reduce((balance, trans) => {
                              const inflowAmt = parseFloat(trans.drValue || "0");
                              const outflowAmt = parseFloat(trans.crValue || "0");
                              return balance + inflowAmt - outflowAmt;
                            }, 0);
                            const currentBalance = previousBalance + transactionAmount;

                            return (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(entry.dateCreated)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {inflow > 0 ? "Debit Transaction" : outflow > 0 ? "Credit Transaction" : "Transaction"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {entry.shopID || "General Transaction"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {inflow > 0 && (
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="bold"
                                      color="success.main"
                                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                    >
                                      <ArrowDownward sx={{ fontSize: 16, mr: 0.5 }} />
                                      {formatCurrency(inflow)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {outflow > 0 && (
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="bold"
                                      color="error.main"
                                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                    >
                                      <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                                      {formatCurrency(outflow)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="medium"
                                    color={currentBalance >= 0 ? 'text.primary' : 'error.main'}
                                  >
                                    {formatCurrency(currentBalance)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {entry.staff_name || "System"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Transaction History Found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  No transaction records available for this account.
                </Typography>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseHistoryDialog} variant="outlined">
              Close
            </Button>
            {fundingHistory.length > 0 && (
              <Button variant="contained" color="primary">
                Download Statement
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Main Content */}
        <Box mb={3}>
          <Typography variant="h4" fontWeight="700" gutterBottom color="primary">
            Sundry Creditors
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track Customers/Vendors outstanding credit balances
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <AccountBalance />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {totalCreditors}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Creditors
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {formatCurrency(totalCredit)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Credit Outstanding
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <MoneyOff />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {filteredCreditors.filter(c => c.total_credit === 0).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Cleared Accounts
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 3,
                pb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Creditors List
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredCreditors.length} vendors with credit balances
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search by name or phone..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={status.loading}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Paper elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Creditor</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }} align="right">Credit Balance</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}></TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCreditors
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((creditor) => (
                      <TableRow
                        key={creditor.user_id}
                        sx={{ 
                          "&:hover": { 
                            backgroundColor: "#f8fafc",
                            transition: 'all 0.2s ease'
                          } 
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center">
                            <Avatar 
                              sx={{ 
                                bgcolor: '#e8f5e9', 
                                width: 40, 
                                height: 40, 
                                mr: 2,
                                fontWeight: 600,
                                color: '#2e7d32'
                              }}
                            >
                              {getInitials(creditor.customer_name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="500">
                                {cleanCustomerName(creditor.customer_name)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {creditor.user_id?.slice(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center">
                            <Phone sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {creditor.phone || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="600"
                            color={creditor.total_credit > 0 ? 'warning.main' : 'success.main'}
                          >
                            {formatCurrency(creditor.total_credit)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={creditor.total_credit === 0 ? "Cleared" : "Credit Due"}
                            color={getCreditSeverity(creditor.total_credit) as any}
                            size="small"
                            variant={creditor.total_credit === 0 ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedCreditor(creditor);
                              setHistoryDialogOpen(true);
                            }}
                          >
                            View Statement
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => handleZeroAccountClick(creditor)}
                            disabled={creditor.total_credit === 0}
                          >
                            Zero Account
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              
              {filteredCreditors.length === 0 && !status.loading && (
                <Box textAlign="center" py={6}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No creditors found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchQuery ? "Try adjusting your search criteria" : "No vendors with credit balances"}
                  </Typography>
                </Box>
              )}
              
              <TablePagination
                component="div"
                count={filteredCreditors.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[100, 150, 200]}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              />
            </Paper>
          </CardContent>
        </Card>
      </IonContent>
    </IonPage>
  );
};

export default SundryCreditorsPage;